import { homedir } from "node:os";
import { join } from "node:path";

export const ORGX_HOSTED_MCP_KEY = "orgx";
export const ORGX_LOCAL_MCP_KEY = "orgx-openclaw";
export const ORGX_HOSTED_MCP_URL = "https://mcp.useorgx.com/mcp";
export const ORGX_CODEX_MCP_URL = ORGX_HOSTED_MCP_URL;
export const ORGX_CODEX_TOOL_PROFILE_HEADER = "x-orgx-tool-profile";
export const ORGX_CODEX_TOOL_PROFILE = "commander";
export const ORGX_HOSTED_MCP_HEALTH_URL = "https://mcp.useorgx.com/health";
export const ORGX_HOSTED_OAUTH_BASE_URL = "https://mcp.useorgx.com";
export const ORGX_HOSTED_OAUTH_AUTHORIZE_URL = `${ORGX_HOSTED_OAUTH_BASE_URL}/authorize`;
export const ORGX_HOSTED_OAUTH_TOKEN_URL = `${ORGX_HOSTED_OAUTH_BASE_URL}/token`;
export const ORGX_HOSTED_OAUTH_REGISTER_URL = `${ORGX_HOSTED_OAUTH_BASE_URL}/register`;
export const ORGX_WIZARD_NPM_PACKAGE_NAME = "@useorgx/wizard";
export const NPM_REGISTRY_BASE_URL = "https://registry.npmjs.org";
export const DEFAULT_OPENCLAW_GATEWAY_PORT = 18_789;
export const DEFAULT_ORGX_BASE_URL =
  process.env.ORGX_BASE_URL?.trim() || "https://useorgx.com";
export const ORGX_WIZARD_OAUTH_CALLBACK_HOST = "127.0.0.1";
export const ORGX_WIZARD_OAUTH_CALLBACK_PATH = "/oauth/callback";
export const ORGX_WIZARD_OAUTH_PREFERRED_PORT = 6_274;
export const ORGX_WIZARD_OAUTH_SCOPES = [
  "decisions:read",
  "decisions:write",
  "agents:read",
  "agents:write",
  "initiatives:read",
  "initiatives:write",
  "memory:read",
  "offline_access",
] as const;
export const ORGX_WIZARD_OAUTH_SCOPE = ORGX_WIZARD_OAUTH_SCOPES.join(" ");

const HOME = homedir();
const APPDATA = process.env.APPDATA?.trim();
const LOCAL_APPDATA = process.env.LOCALAPPDATA?.trim();
const XDG_CONFIG_HOME =
  process.env.XDG_CONFIG_HOME?.trim() || join(HOME, ".config");
export const ORGX_WIZARD_CONFIG_HOME =
  process.env.ORGX_WIZARD_CONFIG_HOME?.trim() ||
  join(XDG_CONFIG_HOME, "useorgx", "wizard");
export const ORGX_WIZARD_AUTH_PATH = join(ORGX_WIZARD_CONFIG_HOME, "auth.json");
export const ORGX_WIZARD_STATE_PATH = join(ORGX_WIZARD_CONFIG_HOME, "state.json");
export const ORGX_SESSION_WORK_CONTEXT_DIR = join(
  ORGX_WIZARD_CONFIG_HOME,
  "session-work-contexts",
);
export const ORGX_SKILL_EXTENSIONS_DIR = join(ORGX_WIZARD_CONFIG_HOME, "skill-extensions");
export const ORGX_WIZARD_KEYTAR_SERVICE = "@useorgx/wizard";
export const ORGX_WIZARD_KEYTAR_ACCOUNT = "orgx-api-key";

function normalizeOrgxApiBaseUrl(baseUrl: string): string {
  const normalized = baseUrl.replace(/\/+$/, "");
  if (/\/api(\/|$)/.test(normalized)) {
    return normalized;
  }
  return `${normalized}/api`;
}

const ORGX_API_BASE_URL = normalizeOrgxApiBaseUrl(DEFAULT_ORGX_BASE_URL);
export const ORGX_SETUP_STATUS_URL = `${ORGX_API_BASE_URL}/setup/status`;
export const ORGX_AUTH_VERIFY_URL = `${ORGX_API_BASE_URL}/auth/verify`;
export const ORGX_CLIENT_SYNC_URL = `${ORGX_API_BASE_URL}/client/sync`;

function uniquePaths(paths: Array<string | undefined>): string[] {
  return [...new Set(paths.filter((path): path is string => Boolean(path)))];
}

export const CLAUDE_DIR = join(HOME, ".claude");
export const CURSOR_DIR = join(HOME, ".cursor");
export const CODEX_DIR = join(HOME, ".codex");
export const OPENCLAW_DIR = join(HOME, ".openclaw");
export const DEEPSEEK_HARNESS_DIR =
  process.env.DSH_HOME?.trim() || join(HOME, ".dsh");
export const AGENTS_DIR = join(HOME, ".agents");
export const CLAUDE_PROJECTS_DIR = join(CLAUDE_DIR, "projects");
export const CODEX_SESSIONS_DIR = join(CODEX_DIR, "sessions");
export const CLAUDE_SKILLS_DIR = join(CLAUDE_DIR, "skills");
export const CLAUDE_ORGX_SKILL_DIR = join(CLAUDE_SKILLS_DIR, "orgx");
export const CLAUDE_ORGX_SKILL_PATH = join(CLAUDE_ORGX_SKILL_DIR, "SKILL.md");
export const CURSOR_RULES_DIR = join(CURSOR_DIR, "rules");
export const CURSOR_ORGX_RULE_PATH = join(CURSOR_RULES_DIR, "orgx.md");
export const CURSOR_PLUGINS_DIR = join(CURSOR_DIR, "plugins", "local");
export const CURSOR_ORGX_PLUGIN_DIR = join(CURSOR_PLUGINS_DIR, "cursor-plugin");
export const CODEX_PLUGINS_DIR = join(CODEX_DIR, "plugins");
export const CODEX_ORGX_PLUGIN_DIR = join(CODEX_PLUGINS_DIR, "orgx-codex-plugin");
export const CODEX_MARKETPLACE_DIR = join(AGENTS_DIR, "plugins");
export const CODEX_MARKETPLACE_PATH = join(CODEX_MARKETPLACE_DIR, "marketplace.json");
export const CLAUDE_MANAGED_MARKETPLACE_DIR = join(
  ORGX_WIZARD_CONFIG_HOME,
  "plugins",
  "claude-marketplace",
);
export const CLAUDE_MANAGED_MARKETPLACE_MANIFEST_PATH = join(
  CLAUDE_MANAGED_MARKETPLACE_DIR,
  ".claude-plugin",
  "marketplace.json",
);
export const CLAUDE_MANAGED_PLUGIN_DIR = join(
  CLAUDE_MANAGED_MARKETPLACE_DIR,
  "plugins",
  "orgx-claude-code-plugin",
);

export const CLAUDE_MCP_PATHS = uniquePaths([join(CLAUDE_DIR, "mcp.json")]);
export const CURSOR_MCP_PATHS = uniquePaths([join(CURSOR_DIR, "mcp.json")]);
export const CODEX_CONFIG_PATHS = uniquePaths([join(CODEX_DIR, "config.toml")]);
export const OPENCLAW_CONFIG_PATHS = uniquePaths([
  join(OPENCLAW_DIR, "openclaw.json"),
]);

export const CLAUDE_MCP_PATH = CLAUDE_MCP_PATHS[0];
export const CURSOR_MCP_PATH = CURSOR_MCP_PATHS[0];
export const CODEX_CONFIG_PATH = CODEX_CONFIG_PATHS[0];
export const OPENCLAW_CONFIG_PATH = OPENCLAW_CONFIG_PATHS[0];

export const CLAUDE_INSTALL_PATHS = uniquePaths([CLAUDE_DIR]);
export const CURSOR_INSTALL_PATHS = uniquePaths([CURSOR_DIR]);
export const CODEX_INSTALL_PATHS = uniquePaths([CODEX_DIR]);
export const OPENCLAW_INSTALL_PATHS = uniquePaths([OPENCLAW_DIR]);
export const DEEPSEEK_HARNESS_PROFILE_PATH = join(
  DEEPSEEK_HARNESS_DIR,
  "profiles",
  "headless",
  "package.json",
);
export const DEEPSEEK_HARNESS_INSTALL_PATHS = uniquePaths([
  DEEPSEEK_HARNESS_DIR,
]);

export const VSCODE_MCP_PATHS = uniquePaths([
  join(HOME, "Library", "Application Support", "Code", "User", "mcp.json"),
  join(XDG_CONFIG_HOME, "Code", "User", "mcp.json"),
  APPDATA ? join(APPDATA, "Code", "User", "mcp.json") : undefined,
]);
export const VSCODE_INSTALL_PATHS = uniquePaths([
  join(HOME, "Library", "Application Support", "Code", "User"),
  join(XDG_CONFIG_HOME, "Code", "User"),
  APPDATA ? join(APPDATA, "Code", "User") : undefined,
  LOCAL_APPDATA ? join(LOCAL_APPDATA, "Programs", "Microsoft VS Code") : undefined,
]);

export const WINDSURF_MCP_PATHS = uniquePaths([
  join(HOME, ".codeium", "windsurf", "mcp_config.json"),
]);
export const WINDSURF_INSTALL_PATHS = uniquePaths([
  join(HOME, ".codeium", "windsurf"),
  LOCAL_APPDATA ? join(LOCAL_APPDATA, "Programs", "Windsurf") : undefined,
]);

export const ZED_SETTINGS_PATHS = uniquePaths([
  join(HOME, "Library", "Application Support", "Zed", "settings.json"),
  join(XDG_CONFIG_HOME, "zed", "settings.json"),
  APPDATA ? join(APPDATA, "Zed", "settings.json") : undefined,
]);
export const ZED_INSTALL_PATHS = uniquePaths([
  join(HOME, "Library", "Application Support", "Zed"),
  join(XDG_CONFIG_HOME, "zed"),
  APPDATA ? join(APPDATA, "Zed") : undefined,
  LOCAL_APPDATA ? join(LOCAL_APPDATA, "Programs", "Zed") : undefined,
]);

export const CHATGPT_APP_PATHS = uniquePaths([
  join(HOME, "Library", "Application Support", "OpenAI"),
  join(HOME, "Library", "Application Support", "com.openai.chat"),
  join(XDG_CONFIG_HOME, "OpenAI"),
  join(XDG_CONFIG_HOME, "openai"),
  APPDATA ? join(APPDATA, "OpenAI") : undefined,
  LOCAL_APPDATA ? join(LOCAL_APPDATA, "OpenAI") : undefined,
]);
